import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, UserCheck, Loader2, Clock, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useFollowStatus } from "@/hooks/useFollowStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FollowButton({ userId, size = "sm", variant, onChange }) {
  const { user } = useAuth();
  const { status, loading, updateLocal } = useFollowStatus(userId, user?.user_id);
  const [busy, setBusy] = useState(false);

  if (!user || !userId || userId === user.user_id) return null;
  const following = !!status?.following;
  const requested = !!status?.requested;
  const isPrivate = !!status?.is_private;

  const toggle = async () => {
    setBusy(true);
    try {
      if (following) {
        await axios.delete(`${API}/users/${userId}/follow`);
        updateLocal({ following: false, followers_count: Math.max(0, (status?.followers_count || 0) - 1) });
        toast.info("Você deixou de seguir");
      } else if (requested) {
        await axios.delete(`${API}/users/${userId}/follow-request`);
        updateLocal({ requested: false });
        toast.info("Solicitação cancelada");
      } else {
        const r = await axios.post(`${API}/users/${userId}/follow`);
        if (r.data?.requested) {
          updateLocal({ requested: true });
          toast.success("Solicitação enviada! Aguarde a aprovação.");
        } else {
          updateLocal({ following: true, requested: false, followers_count: (status?.followers_count || 0) + 1 });
          toast.success("Seguindo!");
        }
      }
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
    setBusy(false);
  };

  const label = following ? "Seguindo" : requested ? "Solicitado" : isPrivate ? "Solicitar para seguir" : "Seguir";
  const Icon = following ? UserCheck : requested ? Clock : isPrivate ? Lock : UserPlus;

  return (
    <Button
      size={size}
      variant={variant || (following || requested ? "outline" : "default")}
      onClick={toggle}
      disabled={busy || loading}
      data-testid={`follow-btn-${userId}`}
      data-state={following ? "following" : requested ? "requested" : "none"}
      className="rounded-full gap-1"
    >
      {busy || loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </Button>
  );
}
